import SEO from "@/common/components/seo";
import FadeIn from "@/common/components/fade-in";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/lib/string";
import useHttpProvider from "@/common/hooks/use-http";
import { useSiteSettings } from "@/common/providers/site-settings";
import { trackViewContent } from "@/common/services/analytics";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { HomeRoute, MerchCartRoute, MerchRoute } from "@/features/home/route";
import { ShoppingCart } from "lucide-react";

import * as StorefrontService from "../services/storefront";
import { StorefrontProduct } from "../models/storefront";
import MerchProductHeader from "../components/merch-product-header";
import {
	findMatchingVariant,
	getColorOptions,
	getVariantAttributesByType,
	parseMerchProductId,
} from "../lib/storefront";
import { useCart } from "../providers/cart";

export default function MerchProductPage() {
	const { slug } = useParams();
	const productId = useMemo(() => parseMerchProductId(slug), [slug]);
	const { http } = useHttpProvider();
	const baseURL = import.meta.env.VITE_SERVER_API_URL;
	const { settings, isLoading: isSettingsLoading } = useSiteSettings();
	const storeEnabled = settings.storeEnabled;
	const cartEnabled = settings.storeEnabled && settings.cartEnabled;
	const { addItem, itemCount } = useCart();

	const [product, setProduct] = useState<StorefrontProduct | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [selectedAttributes, setSelectedAttributes] = useState<
		Record<string, string>
	>({});
	const [quantity, setQuantity] = useState(1);

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			if (!storeEnabled) {
				setProduct(null);
				setIsLoading(false);
				return;
			}

			if (!productId) {
				setProduct(null);
				setIsLoading(false);
				return;
			}

			setIsLoading(true);
			try {
				const p = await StorefrontService.getProductById(http, productId);
				if (cancelled) return;
				setProduct(p);
				// Default to the first available variant so "Add to cart" works without
				// forcing the user to click every option.
				const defaultVariant =
					p?.variants?.find((v) => v.isActive && v.stock > 0) ??
					p?.variants?.find((v) => v.isActive) ??
					p?.variants?.[0];
				const defaults: Record<string, string> = {};
				for (const attr of defaultVariant?.attributes ?? []) {
					defaults[attr.attributeType] = attr.value;
				}
				setSelectedAttributes(defaults);
				setQuantity(1);
			} catch {
				if (cancelled) return;
				setProduct(null);
			} finally {
				if (cancelled) return;
				setIsLoading(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [http, productId, storeEnabled]);

	useEffect(() => {
		if (!product) return;
		trackViewContent({
			content_type: "product",
			content_name: product.name,
			content_ids: [`merch_${product.id}`],
			value: Number(product.basePrice),
			currency: "AUD",
		});
	}, [product]);

	const attributeOptions = useMemo(() => {
		if (!product) return [];
		return getVariantAttributesByType(product.variants);
	}, [product]);

	const requiredAttributeTypes = useMemo(
		() => attributeOptions.map((o) => o.name),
		[attributeOptions]
	);

	const colorOptions = useMemo(() => {
		if (!product) return [];
		return getColorOptions(product.variants);
	}, [product]);

	const isSelectionComplete = useMemo(() => {
		if (requiredAttributeTypes.length === 0) return true;
		return requiredAttributeTypes.every((type) => selectedAttributes[type]);
	}, [requiredAttributeTypes, selectedAttributes]);

	const selectedVariant = useMemo(() => {
		if (!product) return undefined;
		if (!isSelectionComplete) return undefined;
		return findMatchingVariant(product.variants, selectedAttributes);
	}, [product, selectedAttributes, isSelectionComplete]);

	const productImages = useMemo(() => {
		if (!product) return [];
		const sorted = [...product.images].sort((a, b) => {
			if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
			if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
			return a.id - b.id;
		});
		return sorted.map((img) => ({
			id: img.id,
				fullUrl: `${baseURL}/common/uploads/${img.url}`,
			}));
	}, [product, baseURL]);

	const minPrice = useMemo(() => {
		if (!product) return undefined;
		const modifiers = product.variants.map((v) => v.priceModifier ?? 0);
		const minModifier = modifiers.length ? Math.min(...modifiers) : 0;
		return Number(product.basePrice) + Number(minModifier);
	}, [product]);

	const price = useMemo(() => {
		if (!product) return undefined;
		if (!selectedVariant) return minPrice;
		return Number(product.basePrice) + Number(selectedVariant.priceModifier ?? 0);
	}, [product, minPrice, selectedVariant]);

	const badges = useMemo(() => {
		if (!product) return [];
		return [
			...product.categories.map((c) => c.name),
			...product.tags.map((t) => t.name),
		].filter(Boolean);
	}, [product]);

	const isOutOfStock = !!selectedVariant && selectedVariant.stock <= 0;
	const exceedsStock = !!selectedVariant && quantity > selectedVariant.stock;
	const addToCartDisabled =
		!cartEnabled ||
		!selectedVariant ||
		quantity <= 0 ||
		isOutOfStock ||
		exceedsStock;

	const onAddToCart = async () => {
		if (!product || !selectedVariant) return;

		const primaryImage =
			product.images.find((img) => img.isPrimary) ??
			[...product.images].sort((a, b) => a.sortOrder - b.sortOrder)[0];

			await addItem({
				variantId: selectedVariant.id,
				quantity,
				productId: product.id,
				productName: product.name,
			productDescription: product.description ?? null,
			productImageUrl: primaryImage?.url ?? null,
			basePrice: Number(product.basePrice),
			variantName: selectedVariant.name,
			sku: selectedVariant.sku ?? null,
			priceModifier: Number(selectedVariant.priceModifier ?? 0),
			stock: selectedVariant.stock,
			lowStockThreshold: selectedVariant.lowStockThreshold,
				attributes: selectedVariant.attributes ?? [],
			});
		};

	if (isLoading) {
		return (
			<div className="px-4 py-20 text-center md:px-37">
				<p className="web-heading-h3">Loading…</p>
			</div>
		);
	}

	if (!isSettingsLoading && !storeEnabled) {
		return (
			<div className="min-h-screen bg-white">
				<SEO
					title="Store - Reel Rewards"
					description="The Reel Rewards store is currently unavailable."
				/>
				<div className="px-4 py-20 text-center md:px-37">
					<p className="web-heading-h3">Store is currently unavailable</p>
					<p className="web-body-b1 text-gray-700">
						We’re doing some updates behind the scenes.
					</p>
					<div className="mt-6 flex justify-center">
						<Button asChild className="w-full md:w-44">
							<Link to={HomeRoute}>Go Home</Link>
						</Button>
					</div>
				</div>
			</div>
		);
	}

	if (!product) {
		return (
			<div className="px-4 py-20 text-center md:px-37">
				<p className="web-heading-h3">Product not found</p>
				<Link className="web-body-b1 text-blue-500" to={MerchRoute}>
					Back to store
				</Link>
			</div>
		);
	}

	const titleStyle = {
		background:
			"linear-gradient(161deg, #0FF1E5 3.9%, #0DAAEC 33.24%, #0823F8 72.3%, #000D74 116.02%)",
		WebkitBackgroundClip: "text",
		backgroundClip: "text",
		WebkitTextFillColor: "transparent",
	} as const;

	return (
		<div>
			<SEO
				title={`${product.name} - Reel Rewards`}
				description={product.description ?? "Explore the Reel Rewards store."}
				image={productImages[0]?.fullUrl}
			/>

				<MerchProductHeader
					productName={product.name}
					description={product.description}
					basePrice={Number(product.basePrice)}
					price={price}
					images={productImages}
					badges={badges}
				/>

			<FadeIn>
				<div className="bg-bg flex flex-col gap-8 px-4 pb-12 pt-10 md:flex-row md:gap-16 md:px-37 md:pb-16 md:pt-10">
					<div className="flex flex-col gap-8 md:w-3/5">
						<span className="web-heading-h2">
							<span style={titleStyle}>Choose</span> Your Options
						</span>

						{colorOptions.length > 0 && (
							<div className="flex flex-col gap-4 rounded-[32px] bg-white p-6 shadow-sm ring-1 ring-black/5">
								<p className="web-body-b1 text-gray-700">Color</p>
								<div className="flex flex-row flex-wrap items-center gap-3">
									{colorOptions.map((color) => {
										const isSelected = selectedAttributes.Color === color.name;
										return (
											<button
												key={color.name}
												onClick={() =>
													setSelectedAttributes((prev) => ({
														...prev,
														Color: color.name,
													}))
												}
												className={`h-10 w-10 rounded-full border-2 ${
													isSelected ? "border-blue-500" : "border-transparent"
												}`}
												style={{
													backgroundColor: color.hex ?? "#E5E7EB",
												}}
												title={color.name}
											/>
										);
									})}
								</div>
								{selectedAttributes.Color && (
									<p className="web-body-b2 text-gray-500">
										Selected: {selectedAttributes.Color}
									</p>
								)}
							</div>
						)}

							{attributeOptions
								.filter((opt) => opt.name !== "Color")
								.map((opt) => (
									<div
									key={opt.name}
									className="flex flex-col gap-4 rounded-[32px] bg-white p-6 shadow-sm ring-1 ring-black/5"
								>
									<p className="web-body-b1 text-gray-700">{opt.name}</p>
									<div className="flex flex-wrap gap-3">
										{opt.values.map((value) => {
											const isSelected = selectedAttributes[opt.name] === value;
											return (
												<button
													key={value}
													onClick={() =>
														setSelectedAttributes((prev) => ({
															...prev,
															[opt.name]: value,
														}))
													}
													className={`rounded-full border px-4 py-2 text-sm ${
														isSelected
															? "border-blue-500 bg-blue-50 text-blue-700"
															: "border-gray-200 text-gray-700"
													}`}
												>
													{value}
												</button>
											);
										})}
									</div>
									</div>
								))}

							<Link className="web-body-b1 text-blue-500" to={MerchRoute}>
								Back to store
							</Link>
						</div>

						<div className="flex flex-col gap-6 rounded-[32px] bg-white p-6 shadow-sm ring-1 ring-black/5 md:w-2/5 md:self-start">
							<span className="web-heading-h3">Add to cart</span>

							<div className="flex items-center justify-between">
								<p className="web-body-b1 text-gray-700">Price</p>
								<p className="web-heading-h4 text-blue-500">
								{price != null ? formatPrice(price, "AUD") : "—"}
							</p>
						</div>

						{!cartEnabled ? (
							<div className="flex gap-3 rounded-[24px] bg-[rgba(46,_127,_219,_0.1)] p-4">
								<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white">
									<ShoppingCart className="h-5 w-5 text-blue-700" />
								</div>
								<div className="flex min-w-0 flex-col gap-1">
									<p className="web-body-b1-medium text-gray-900">
										Cart is currently disabled
									</p>
									<p className="web-body-b2 text-gray-700">
										You can still browse options, but purchasing is paused right
										now.
									</p>
									<Link className="web-body-b2 w-fit text-blue-700" to={MerchRoute}>
										Back to store
									</Link>
								</div>
							</div>
						) : (
							<>
								{attributeOptions.length > 0 && !isSelectionComplete && (
									<p className="web-body-b2 text-gray-500">
										Select all options to add to cart.
									</p>
								)}

								{isSelectionComplete &&
									!selectedVariant &&
									attributeOptions.length > 0 && (
										<p className="web-body-b2 text-gray-500">
											That combination isn’t available.
										</p>
									)}

								{selectedVariant && (
									<div className="flex flex-col gap-2 rounded-[24px] bg-gray-50 p-4">
										<p className="web-body-b1-medium text-gray-800">
											{selectedVariant.name}
										</p>
										{isOutOfStock ? (
											<p className="web-body-b2 text-red-600">
												Out of stock
											</p>
										) : (
											<p className="web-body-b2 text-gray-500">
												Stock: {selectedVariant.stock}
												{selectedVariant.stock <=
													selectedVariant.lowStockThreshold &&
												selectedVariant.stock > 0
													? ` (low: ${selectedVariant.lowStockThreshold})`
													: ""}
											</p>
										)}
									</div>
								)}

								<div className="flex items-center justify-between">
									<p className="web-body-b1 text-gray-700">Quantity</p>
									<div className="flex items-center gap-3">
										<Button
											variant="secondary"
											className="h-10 w-10 rounded-full p-0"
											onClick={() => setQuantity((q) => Math.max(1, q - 1))}
											disabled={quantity <= 1}
										>
											-
										</Button>
										<span className="web-body-b1 w-6 text-center">
											{quantity}
										</span>
										<Button
											variant="secondary"
											className="h-10 w-10 rounded-full p-0"
											onClick={() =>
												setQuantity((q) =>
													selectedVariant
														? Math.min(selectedVariant.stock, q + 1)
														: q + 1
												)
											}
											disabled={
												!selectedVariant ||
												quantity >= selectedVariant.stock
											}
										>
											+
										</Button>
									</div>
								</div>

								<Button
									className="w-full"
									onClick={onAddToCart}
									disabled={addToCartDisabled}
								>
									Add to cart
								</Button>

								<Button asChild variant="secondary" className="w-full">
									<Link to={MerchCartRoute} className="gap-3">
										View cart
										{itemCount > 0 && (
											<span
												className="inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-blue-700 px-2 text-xs font-bold leading-none text-white"
												aria-label={`${itemCount} item${itemCount === 1 ? "" : "s"} in cart`}
											>
												{itemCount > 99 ? "99+" : itemCount}
											</span>
										)}
									</Link>
								</Button>
							</>
						)}
						</div>
					</div>
				</FadeIn>
			</div>
	);
}
